//@ts-check
import { ReactNode, useEffect } from 'react';
import React, { Component } from 'react';
import { isProduction, themeInfo } from '../environment';
import { heapTrack, reportErrorToNewRelic } from '../helpers';

// ? [REVISIT] ensure these get reported to NR in production if that is not already the case
// * [IMPROVEMENT] generic implementation; move into a 'shared'/'utils' folder to be used across react components (once we get there)
type TState = {
  error: Error | null;
};
type TProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: Error, errorInfo: any) => void;
  errorRender?: (error: Error, resetError: () => void) => ReactNode;
  className?: string;
};

class ErrorBoundary extends Component<TProps, TState> {
  state: TState = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  //@ts-expect-error // ? [REVISIT]
  componentDidCatch(error, errorInfo) {
    const { onError } = this.props;
    if (onError) onError(error, errorInfo);
    defaultErrorHandling(error, errorInfo);
  }

  resetError = () => this.setState({ error: null });

  render() {
    const {
      props: { errorRender, className },
      resetError,
      state: { error },
    } = this;

    return !error
      ? this.props.children
      : errorRender?.(error, resetError) ?? <DefaultErrorUI className={className} resetError={resetError} />;
  }
}

export default ErrorBoundary;

// Hof higher order function
export const logErrorBoundaryToExternalServicesHof = (additionalProperties: NewRelicCustomAttributes = {}) => (
  error: Error,
  errorInfo: unknown
) => logErrorBoundaryToExternalServices(error, errorInfo, additionalProperties);

const logErrorBoundaryToExternalServices = (
  error: Error,
  errorInfo: unknown,
  additionalProperties: NewRelicCustomAttributes = {}
) => {
  const componentStack =
    typeof errorInfo === 'object' && errorInfo && 'componentStack' in errorInfo
      ? String((errorInfo as { componentStack?: unknown }).componentStack || '')
      : '';

  reportErrorToNewRelic(error, {
    ...additionalProperties,
    url: window.location.pathname,
    errMessage: error?.message,
    componentStack: componentStack || undefined,
  });

  heapTrack('error_boundary_did_catch', {
    ...additionalProperties,
    url: window.location.pathname,
    error,
    errorInfo,
    errMessage: error?.message,
    themeInfo,
  });
};

//@ts-expect-error // ? [REVISIT]
export const defaultErrorHandling = (error, errorInfo) => {
  if (isProduction) {
    window?.requestIdleCallback?.(
      () => {
        logErrorBoundaryToExternalServices(error, errorInfo);
      },
      { timeout: 5000 }
    );
  } else {
    console.error({ error, errorInfo });
  }
};

export const DefaultErrorUI = ({ resetError, className }: { resetError: () => void; className?: string }) =>
  !isProduction ? (
    <div className={`DefaultErrorUI ${className || ''}`}>
      <h3>Something went wrong.</h3>
      {!isProduction && <div>check your dev console</div>}
      <button onClick={resetError}>Retry</button>
    </div>
  ) : null;

export const ExplicitDeadEnd = () => {
  useEffect(() => {
    heapTrack('explicit_dead_end', {
      url: window.location.href,
      themeInfo,
    });
  }, []);
  return <DefaultErrorUI resetError={() => {}} />;
};
